import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SiteLayout } from './components/site/SiteLayout';
import { ScrollToTop } from './components/site/ScrollToTop';
import { PageRenderer } from './components/site/PageRenderer';
import { Seo } from './components/site/Seo';
import { useSiteContent } from './context/SiteContentContext';
import { LeadershipPage } from './pages/LeadershipPage';
import { CareersPage } from './pages/CareersPage';
import { CaseStudiesPage } from './pages/CaseStudiesPage';
import { CaseStudyDetailPage } from './pages/CaseStudyDetailPage';
import { ContactPage } from './pages/ContactPage';
import { FaqPage } from './pages/FaqPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { RequestDemoPage } from './pages/RequestDemoPage';
import { ResourceDetailPage } from './pages/ResourceDetailPage';
import { TechnologyPage } from './pages/TechnologyPage';
import { LATEST_SECTION_HASH } from './lib/siteRoutes';
import './App.scss';

function RoutedPage({ pageKey }) {
  const { siteContent, contentStatus } = useSiteContent();
  const page = siteContent.pages[pageKey];

  if (!page) {
    if (contentStatus === 'loading') {
      return null;
    }
    return <NotFoundPage />;
  }

  return (
    <>
      <Seo page={page} title={pageKey === 'home' ? 'Home | Rivelin' : undefined} />
      <PageRenderer page={page} />
    </>
  );
}

function AppRoutes() {
  const { siteContent } = useSiteContent();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <SiteLayout siteSettings={siteContent.siteSettings} jobs={siteContent.collections.jobs || []}>
        <Routes>
          <Route path='/' element={<RoutedPage pageKey='home' />} />
          <Route path='/products' element={<RoutedPage pageKey='solutions' />} />
          <Route path='/products/r1000' element={<RoutedPage pageKey='r1000Product' />} />
          <Route path='/products/r500' element={<RoutedPage pageKey='r500Product' />} />
          <Route path='/products/robotic-roof-module' element={<RoutedPage pageKey='roboticRoofModuleProduct' />} />
          <Route path='/technology' element={<TechnologyPage />} />
          <Route path='/solutions' element={<Navigate replace to='/products' />} />
          <Route path='/solutions/r1000' element={<Navigate replace to='/products/r1000' />} />
          <Route path='/solutions/r500' element={<Navigate replace to='/products/r500' />} />
          <Route path='/solutions/robotic-roof-module' element={<Navigate replace to='/products/robotic-roof-module' />} />
          <Route path='/solutions/netshape' element={<Navigate replace to='/technology' />} />
          <Route path='/technologies' element={<TechnologyPage />} />
          <Route path='/our-story' element={<RoutedPage pageKey='company' />} />
          <Route path='/company' element={<Navigate replace to='/our-story' />} />
          <Route path='/careers' element={<CareersPage />} />
          <Route path='/jobs' element={<Navigate replace to={{ pathname: '/careers', hash: '#open-roles' }} />} />
          <Route path='/jobs/:slug' element={<JobDetailPage />} />
          <Route path='/open-roles' element={<Navigate replace to={{ pathname: '/careers', hash: '#open-roles' }} />} />
          <Route path='/resources' element={<Navigate replace to={{ pathname: '/', hash: LATEST_SECTION_HASH }} />} />
          <Route path='/resources/:slug' element={<ResourceDetailPage />} />
          <Route path='/case-studies' element={<CaseStudiesPage />} />
          <Route path='/case-studies/:slug' element={<CaseStudyDetailPage />} />
          <Route path='/faq' element={<FaqPage />} />
          <Route path='/contact' element={<ContactPage />} />
          <Route path='/request-demo' element={<RequestDemoPage />} />
          <Route path='/privacy-policy' element={<PrivacyPolicyPage />} />

          <Route path='/home-page' element={<Navigate replace to='/' />} />
          <Route path='/who-we-are' element={<Navigate replace to='/our-story' />} />
          <Route path='/why-we-exist' element={<Navigate replace to='/products' />} />
          <Route path='/leadership' element={<LeadershipPage />} />
          <Route path='/team' element={<Navigate replace to='/leadership' />} />
          <Route path='/meet-the-robots' element={<Navigate replace to='/products/r1000' />} />
          <Route path='/learn-the-tech' element={<Navigate replace to='/products/r1000' />} />
          <Route path='/see-it-in-action' element={<Navigate replace to='/case-studies' />} />
          <Route path='/request-a-demo' element={<Navigate replace to='/request-demo' />} />
          <Route path='/contact-us' element={<Navigate replace to='/contact' />} />
          <Route path='/datasheet' element={<Navigate replace to='/request-demo' />} />
          <Route path='/r1000' element={<Navigate replace to='/products/r1000' />} />
          <Route path='/r500' element={<Navigate replace to='/products/r500' />} />
          <Route path='/robotic-roof-module' element={<Navigate replace to='/products/robotic-roof-module' />} />

          <Route path='*' element={<NotFoundPage />} />
        </Routes>
      </SiteLayout>
    </BrowserRouter>
  );
}

export default function App() {
  return <AppRoutes />;
}
